import { BarChartOutlined } from '@ant-design/icons';
import { Card, Typography } from 'antd';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../constants/styles';
import { TrendDirection } from '../../types';
import { SkewedBadge } from '../SkewedBadge';
import { TabLinkButton } from '../TabLinkButton';
import { TrendIndicator } from '../TrendIndicator';

const { Text } = Typography;

const ChurnRateBox: FC<{
  label: string;
  rate: number;
  status: { color: string; border: string; label: string };
  trend: TrendDirection;
  trendColor: string;
  loading: boolean;
  contentWidth: number;
  onDetailsClick: () => void;
}> = ({
  label,
  rate,
  status,
  trend,
  trendColor,
  loading,
  contentWidth,
  onDetailsClick,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.churnCard');
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: BRAND_COLORS.primary.royalBlue,
        border: `1px solid ${BRAND_COLORS.secondary.steelBlue}`,
        borderRadius: 6,
        padding: 16,
      }}
    >
      <Text
        style={{
          color: BRAND_COLORS.secondary.lightSage,
          fontSize: 12,
          marginBottom: 4,
        }}
      >
        {label}
      </Text>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 40,
              fontWeight: 700,
              color: status.color,
              lineHeight: 1,
            }}
          >
            {loading ? '...' : `${Math.round(rate)}%`}
          </span>
          <TrendIndicator
            direction={trend}
            color={trendColor}
            label={trend.toUpperCase()}
          />
        </div>
        <SkewedBadge
          color={status.color}
          border={status.border}
          label={status.label}
        />
        <div style={{ marginTop: 8 }}>
          <TabLinkButton
            onClick={onDetailsClick}
            fontSize={10}
            iconColor={status.color}
            icon={<BarChartOutlined />}
            label={t('detailsLabel')}
            tooltip={t('detailsTooltip')}
            windowWidth={contentWidth}
          />
        </div>
      </div>
    </div>
  );
};

export interface IDonorChurnRateCardProps {
  metricsLoading: boolean;
  periodChurnRate: number;
  quarterlyChurnRate: number;
  periodChurnStatus: { color: string; border: string; label: string };
  quarterlyChurnStatus: { color: string; border: string; label: string };
  dateRangeLabel: string;
  churnTrend: TrendDirection;
  churnTrendColor: string;
  churnQuarterlyTrend: TrendDirection;
  churnQuarterlyTrendColor: string;
  contentWidth: number;
  onPeriodDetailsClick: () => void;
  onQuarterlyDetailsClick: () => void;
}

export const DonorChurnRateCard: FC<IDonorChurnRateCardProps> = ({
  metricsLoading,
  periodChurnRate,
  quarterlyChurnRate,
  periodChurnStatus,
  quarterlyChurnStatus,
  dateRangeLabel,
  churnTrend,
  churnTrendColor,
  churnQuarterlyTrend,
  churnQuarterlyTrendColor,
  contentWidth,
  onPeriodDetailsClick,
  onQuarterlyDetailsClick,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.churnCard');
  return (
    <Card
      size="small"
      style={{ height: '100%', background: BRAND_COLORS.primary.deepNavy }}
    >
      <Text style={{ color: '#fff', display: 'block', textAlign: 'center' }}>
        {t('title')}
      </Text>
      <div style={{ marginTop: 8 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            margin: 16,
          }}
        >
          <ChurnRateBox
            label={t('currentPeriodLabel', { dateRangeLabel })}
            rate={periodChurnRate}
            status={periodChurnStatus}
            trend={churnTrend}
            trendColor={churnTrendColor}
            loading={metricsLoading}
            contentWidth={contentWidth}
            onDetailsClick={onPeriodDetailsClick}
          />
          <ChurnRateBox
            label={t('quarterlyLabel')}
            rate={quarterlyChurnRate}
            status={quarterlyChurnStatus}
            trend={churnQuarterlyTrend}
            trendColor={churnQuarterlyTrendColor}
            loading={metricsLoading}
            contentWidth={contentWidth}
            onDetailsClick={onQuarterlyDetailsClick}
          />
        </div>
      </div>
    </Card>
  );
};
